import { useMemo } from 'react'

import { useFilters, useModals, useSetState } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import handleCallbackAction from '@functions/handleCallbackAction'

import CreateOrEditCreditNoteModal from '@modals/CreateOrEditCreditNoteModal'

import type { CreditNoteModel } from '@models/types'

import PageContent from './components/PageContent'

const defaultState = {
  showCreateOrEditCreditNoteModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

/* eslint-disable no-undef */
const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    CreateOrEditCreditNoteModal: {
      closeAllModals: () => setState({
        showCreateOrEditCreditNoteModal: false,
      }),
      closeModal: () => setState({ showCreateOrEditCreditNoteModal: false }),
      createCreditNote: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      createCreditNoteItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateCreditNote: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const CampaignStatement = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    showCreateOrEditCreditNoteModal,
  } = state

  const {
    callbacks: filterCallbacks,
    defaultFilters,
    defaultFiltersModifiedAt,
    pageFilters,
  } = useFilters({ defaultFilters: { itemType: 'campaign_balance' } })

  const modalContext = useModals({ setState })
  const { callbacks: { setModalData } } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      ...filterCallbacks,
      showCreateOrEditCreditNoteModal: (payload: { creditNote?: CreditNoteModel }) => {
        setModalData('CreateOrEditCreditNoteModal', payload)
        setState({ showCreateOrEditCreditNoteModal: true })
      },
    },
    defaultFilters,
  }), [defaultFiltersModifiedAt])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent pageFilters={pageFilters} />

        <CreateOrEditCreditNoteModal
          callbacks={callbacks('CreateOrEditCreditNoteModal', setState)}
          showModal={showCreateOrEditCreditNoteModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default CampaignStatement
